import {
	type PoExport,
	type PoProposal,
	type PoRun,
	poAcceptProposal,
	poBulkAcceptProposals,
	poCorrectProposal
} from '@repo/sdk/gen'
import { zPoListExportsData, zPoProposalViewFilters } from '@repo/sdk/zod'
import {
	DataTable,
	useDataTableColumnState
} from '@repo/ui/commons/data-table/data-table'
import type { DataTableColumn } from '@repo/ui/commons/data-table/types'
import { InlineFilterAsyncSelect } from '@repo/ui/commons/inline-filters/inline-filter-async-options'
import { makeBreadcrumbFromCache } from '@repo/ui/commons/query-breadcrumbs'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle
} from '@repo/ui/empty'
import { Spinner } from '@repo/ui/spinner'
import { Tabs, TabsList, TabsTrigger } from '@repo/ui/tabs'
import { getErrorCode } from '@repo/ui/utils'
import {
	useMutation,
	useQuery,
	useQueryClient,
	useSuspenseQuery
} from '@tanstack/react-query'
import {
	createFileRoute,
	stripSearchParams,
	useNavigate
} from '@tanstack/react-router'
import { AlertTriangle, SearchX } from 'lucide-react'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'
import { pageTitle, type TKey, translateApp, useTranslation } from '@/lib/i18n'
import { makeQueryKeys } from '@/lib/query-keys'
import { CorrectionsWorkspace } from '@/modules/corrections/components/corrections-workspace'
import {
	correctionSearchSchema,
	stripRemovedCorrectionFilters
} from '@/modules/corrections/data/view'
import { AcceptViewControl } from '@/routes/(private)/documents/-components/accept-view-control'
import { CorrectionDialog } from '@/routes/(private)/documents/-components/correction-dialog'
import { ExportDialog } from '@/routes/(private)/documents/-components/export-dialog'
import {
	EXPORT_SORT_FIELDS,
	ExportsTable
} from '@/routes/(private)/documents/-components/exports-table'
import {
	AdoptionsCell,
	CorrectedQtyCell,
	IsbnCell,
	NoteCell,
	PredSoldCell,
	ProposedQtyChip,
	ReviewCell,
	SoldHistoryCell,
	TitleCell
} from '@/routes/(private)/documents/-components/proposal-cells'
import { ProposalDetailPanel } from '@/routes/(private)/documents/-components/proposal-detail-panel'
import { RenameRunDialog } from '@/routes/(private)/documents/-components/rename-run-dialog'
import { WorkspaceHeader } from '@/routes/(private)/documents/-components/workspace-header'
import { WorkspaceToolbar } from '@/routes/(private)/documents/-components/workspace-toolbar'
import { poErrorMessage } from '@/routes/(private)/documents/-lib/error-message'
import { NOTICE_TONES } from '@/routes/(private)/documents/-lib/notice-tones'
import {
	poExportAuthorsQueryOptions,
	proposalsListQueryOptions,
	runDetailQueryOptions
} from '@/routes/(private)/documents/-lib/query-options'
import {
	PROPOSAL_SEARCH,
	PROPOSAL_SORT_FIELDS,
	viewFilters
} from '@/routes/(private)/documents/-lib/review-view'
import { usePageSize } from '@/stores/use-page-size'

const EMPTY_PROPOSALS: PoProposal[] = []
const REVIEW_VALUES = ['pending', 'accepted', 'corrected'] as const
const SCHOOL_TYPE_VALUES = [
	'elementari',
	'medie',
	'superiori',
	'varia'
] as const
const SCHOOL_TYPE_LABEL_KEYS: Record<
	string,
	`po:school_type.${(typeof SCHOOL_TYPE_VALUES)[number]}`
> = {
	elementari: 'po:school_type.elementari',
	medie: 'po:school_type.medie',
	superiori: 'po:school_type.superiori',
	varia: 'po:school_type.varia'
}
const TABS = ['proposte', 'correzioni', 'export'] as const
const EXPORT_STATUS_LABEL_KEYS = {
	pending: 'po:exports.pending',
	completed: 'po:exports.completed',
	failed: 'po:exports.failed'
} as const satisfies Record<PoExport['status'], TKey>

// A value the URL cannot hold (an old link, a hand-edited address) falls back
// to its default instead of failing the page.
const searchSchema = z.object({
	...correctionSearchSchema.shape,
	tab: z.enum(TABS).default('proposte').catch('proposte'),
	// The row whose detail is open next to the grid.
	row: z.string().optional().catch(undefined),
	// Within the backend's limits: a longer search, or a publisher id past
	// int32, would make the grid fail.
	search: PROPOSAL_SEARCH.trim().default('').catch(''),
	publisher: zPoProposalViewFilters.shape.publisherIds
		.unwrap()
		.element.optional()
		.catch(undefined),
	distributor: z.string().optional().catch(undefined),
	tipologia: z.enum(SCHOOL_TYPE_VALUES).optional().catch(undefined),
	review: z.enum(REVIEW_VALUES).optional().catch(undefined),
	by: z.enum(PROPOSAL_SORT_FIELDS).optional().catch(undefined),
	direction: z.enum(['asc', 'desc']).optional().catch(undefined),
	page: z.number().int().min(1).default(1).catch(1),
	// The export tab pages independently of the proposals grid.
	exportPage: z.number().int().min(1).default(1).catch(1),
	exportBy: z.enum(EXPORT_SORT_FIELDS).optional().catch(undefined),
	exportDirection: z.enum(['asc', 'desc']).optional().catch(undefined),
	exportStatus: z
		.enum(['pending', 'completed', 'failed'])
		.optional()
		.catch(undefined),
	exportAuthor: zPoListExportsData.shape.body.shape.filters
		.unwrap()
		.shape.generatedByIds.unwrap()
		.element.optional()
		.catch(undefined)
})

export const Route = createFileRoute('/(private)/documents/$runId')({
	component: PoDocumentDetail,
	validateSearch: searchSchema,
	search: {
		middlewares: [
			stripRemovedCorrectionFilters,
			stripSearchParams(searchSchema.parse({}))
		]
	},
	loader: ({ context, params }) =>
		context.queryClient.ensureQueryData(
			runDetailQueryOptions(params.runId)
		),
	staticData: {
		breadcrumb: ({ params, queryClient }) =>
			makeBreadcrumbFromCache<{ name: string } | null>(
				queryClient,
				makeQueryKeys.po.runDetail(params.runId),
				(run) => run?.name ?? 'Documento'
			)
	},
	head: ({ loaderData }) => ({
		meta: [
			{
				title: pageTitle(
					loaderData?.name ??
						translateApp('it', 'po:detail_breadcrumb')
				)
			}
		]
	})
})

// The export history of this document: its own slice of URL state (page, sort,
// status, author), kept out of the detail component so the grid's own filters and this
// tab's never share a name.
function DocumentExportsTab({ runId }: { runId: string }) {
	const { t } = useTranslation()
	const params = Route.useSearch()
	const navigate = useNavigate({ from: Route.fullPath })
	const authors = useQuery(poExportAuthorsQueryOptions(runId))
	const selectedAuthor = authors.data?.find(
		(author) => author.id === params.exportAuthor
	)

	return (
		<div className='flex min-h-0 flex-1 animate-enter flex-col px-4 pb-3 [--enter-step:2] md:px-6'>
			{/* Same filter bar as the proposals toolbar. */}
			<div className='py-2'>
				<div className='flex flex-wrap items-center gap-1.5 rounded-lg bg-muted/70 p-1.5 2xl:gap-2 [&_[data-slot=input-group]]:bg-background'>
					<InlineFilterAsyncSelect
						label={t('po:exports.columns.status')}
						onValueChange={(exportStatus) => {
							navigate({
								search: (prev) => ({
									...prev,
									exportStatus: exportStatus ?? undefined,
									exportPage: 1
								}),
								replace: true
							})
						}}
						source={{
							items: searchSchema.shape.exportStatus
								.unwrap()
								.unwrap().options,
							getKey: (status) => status,
							getLabel: (status) =>
								t(EXPORT_STATUS_LABEL_KEYS[status]),
							placeholder: t('po:exports.all_statuses')
						}}
						value={params.exportStatus ?? null}
					/>
					<InlineFilterAsyncSelect
						label={t('po:exports.columns.generated_by')}
						onValueChange={(author) => {
							navigate({
								search: (prev) => ({
									...prev,
									exportAuthor: author?.id,
									exportPage: 1
								}),
								replace: true
							})
						}}
						source={{
							items: authors.data ?? [],
							isPending: authors.isPending,
							isError: authors.isError,
							getKey: (author) => author.id,
							getLabel: (author) =>
								author.name ??
								t('po:exports.generated_by_unavailable'),
							placeholder: t('po:exports.all_authors')
						}}
						value={
							selectedAuthor ??
							(params.exportAuthor
								? { id: params.exportAuthor, name: null }
								: null)
						}
					/>
				</div>
			</div>
			<ExportsTable
				generatedById={params.exportAuthor}
				onPageChange={(exportPage) =>
					navigate({
						search: (prev) => ({ ...prev, exportPage }),
						replace: true
					})
				}
				onSortChange={(sort) =>
					navigate({
						search: (prev) => ({
							...prev,
							exportBy: sort.by,
							exportDirection: sort.direction,
							exportPage: 1
						}),
						replace: true
					})
				}
				page={params.exportPage}
				runId={runId}
				sort={{
					by: params.exportBy,
					direction: params.exportDirection
				}}
				status={params.exportStatus}
			/>
		</div>
	)
}

function PoDocumentDetail() {
	const { t } = useTranslation()
	const { runId } = Route.useParams()
	const params = Route.useSearch()
	const navigate = useNavigate({ from: Route.fullPath })
	const queryClient = useQueryClient()
	const pageSize = usePageSize((s) => s.pageSize)
	const setPageSize = usePageSize((s) => s.setPageSize)

	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
	const [correction, setCorrection] = useState<{
		proposal: PoProposal
		newQty: number
	} | null>(null)
	const [renameOpen, setRenameOpen] = useState(false)
	const [exportOpen, setExportOpen] = useState(false)

	const run = useSuspenseQuery(runDetailQueryOptions(runId)).data
	// The engine has handed the document over: it has rows to show. The engine
	// version comes only with its result, so a document that failed stays out
	// even once archived. Reviewing the rows is another matter — only a
	// document still in lavorazione is editable.
	const generated = run !== undefined && run.engineVersion !== null
	const editable = run?.status === 'in_progress'
	// Which panel is on screen. Resolved once: the three blocks below would
	// otherwise each re-test that the document exists and has rows.
	const tab = generated ? params.tab : null

	const proposalsQuery = useQuery({
		...proposalsListQueryOptions({
			page: params.page,
			limit: pageSize,
			filters: { ...viewFilters(params), runId },
			sort: params.by
				? { field: params.by, direction: params.direction ?? 'asc' }
				: undefined
		}),
		enabled: generated
	})

	const invalidateRun = async () => {
		await queryClient.invalidateQueries({
			queryKey: makeQueryKeys.po.proposalsAll(runId)
		})
		await queryClient.invalidateQueries({
			queryKey: makeQueryKeys.po.proposalAll()
		})
		await queryClient.invalidateQueries({
			queryKey: makeQueryKeys.po.runsAll()
		})
	}

	// A refused decision means this view is behind the database: the row was
	// corrected in another tab, or the document was completed or archived
	// meanwhile. The message says which, and the reload shows it.
	const refuseReview = async (error: unknown) => {
		toast.error(poErrorMessage(error))
		await invalidateRun()
	}

	const acceptProposal = useMutation({
		mutationFn: (body: { id: string }) =>
			poAcceptProposal({ body: { data: body } }),
		onSuccess: invalidateRun,
		onError: refuseReview
	})

	const correctProposal = useMutation({
		mutationFn: (body: {
			id: string
			correctedQty: number
			note: string
		}) => poCorrectProposal({ body: { data: body } }),
		onSuccess: async () => {
			await invalidateRun()
			await queryClient.invalidateQueries({
				queryKey: makeQueryKeys.po.correctionsAll()
			})
			setCorrection(null)
		},
		onError: async (error) => {
			await refuseReview(error)
			// On a closed document there is nothing left to correct; any other
			// failure keeps the dialog and its note for a retry.
			if (getErrorCode(error) === 'run_not_editable') {
				setCorrection(null)
			}
		}
	})

	const bulkAccept = useMutation({
		mutationFn: (ids: string[]) =>
			poBulkAcceptProposals({ body: { data: { runId, ids } } }),
		onSuccess: async (response) => {
			await invalidateRun()
			setSelectedIds(new Set())
			toast.success(
				t('po:grid.bulk_accepted', { count: response.accepted })
			)
		},
		onError: refuseReview
	})

	// The detail panel follows the row kept in the URL: reloadable and shareable.
	const openRow = (id: string) =>
		navigate({ search: (prev) => ({ ...prev, row: id }), replace: true })

	const columns: DataTableColumn<
		PoProposal,
		keyof typeof PROPOSAL_SORT_FIELDS
	>[] = [
		{
			id: 'code',
			header: t('po:grid.columns.code'),
			pinned: 'left',
			cell: (dataItem) => (
				<IsbnCell
					code={dataItem.libro}
					isbn={dataItem.isbn || dataItem.bookCode || ''}
				/>
			),
			minWidth: 156,
			flex: 1
		},
		{
			id: 'title',
			header: t('po:grid.columns.title'),
			pinned: 'left',
			sortKey: PROPOSAL_SORT_FIELDS.title,
			cell: (dataItem) => (
				<TitleCell
					onOpen={() => openRow(dataItem.id)}
					proposal={dataItem}
				/>
			),
			minWidth: 308,
			flex: 3.3
		},
		{
			id: 'soldPrevYear',
			header: t('po:grid.columns.sold_history'),
			description: t('po:grid.sold_history_title'),
			sortKey: PROPOSAL_SORT_FIELDS.soldPrevYear,
			cell: (dataItem) => <SoldHistoryCell proposal={dataItem} />,
			minWidth: 220,
			flex: 1.1
		},
		{
			id: 'predSold',
			header: t('po:grid.columns.pred_sold'),
			description: t('po:grid.pred_sold_title'),
			sortKey: PROPOSAL_SORT_FIELDS.predSoldPrevYear,
			cell: (dataItem) => <PredSoldCell proposal={dataItem} />,
			minWidth: 200,
			flex: 1.1
		},
		{
			id: 'schoolType',
			header: t('po:grid.columns.school_type'),
			cell: (dataItem) =>
				t(
					SCHOOL_TYPE_LABEL_KEYS[dataItem.schoolType] ??
						SCHOOL_TYPE_LABEL_KEYS.varia
				),
			minWidth: 116,
			flex: 1
		},
		{
			id: 'adoptions',
			header: t('po:grid.columns.adoptions'),
			description: t('po:grid.adoptions_title'),
			sortKey: PROPOSAL_SORT_FIELDS.adoptions,
			cell: (dataItem) => <AdoptionsCell proposal={dataItem} />,
			minWidth: 150,
			flex: 1
		},
		{
			id: 'stockReal',
			header: t('po:grid.columns.stock_real'),
			description: t('po:grid.stock_title'),
			sortKey: PROPOSAL_SORT_FIELDS.stockReal,
			cell: (dataItem) => dataItem.stock.toLocaleString('it-IT'),
			minWidth: 140,
			flex: 1
		},
		{
			id: 'stock',
			header: t('po:grid.columns.stock'),
			description: t('po:grid.available_title'),
			sortKey: PROPOSAL_SORT_FIELDS.stock,
			cell: (dataItem) =>
				Math.max(0, dataItem.stock - dataItem.committed).toLocaleString(
					'it-IT'
				),
			minWidth: 150,
			flex: 1
		},
		{
			id: 'proposedQty',
			header: t('po:grid.columns.proposed'),
			sortKey: PROPOSAL_SORT_FIELDS.proposedQty,
			cell: (dataItem) => (
				<ProposedQtyChip
					onOpen={() => openRow(dataItem.id)}
					qty={dataItem.proposedQty}
				/>
			),
			minWidth: 136,
			flex: 1
		},
		{
			id: 'correctedQty',
			header: t('po:grid.columns.corrected'),
			sortKey: PROPOSAL_SORT_FIELDS.correctedQty,
			cell: (dataItem) => (
				<CorrectedQtyCell
					editable={editable}
					onCorrect={(qty) =>
						setCorrection({ proposal: dataItem, newQty: qty })
					}
					proposal={dataItem}
				/>
			),
			minWidth: 130,
			flex: 1
		},
		{
			id: 'note',
			header: t('po:grid.columns.note'),
			cell: (dataItem) => <NoteCell note={dataItem.feedbackNote} />,
			minWidth: 150,
			flex: 2
		},
		{
			id: 'reviewStatus',
			header: t('po:grid.columns.status'),
			pinned: 'right',
			hideable: false,
			manageable: false,
			pinnable: false,
			enableResizing: false,
			cell: (dataItem) => (
				<ReviewCell
					busy={acceptProposal.isPending}
					onAccept={() => acceptProposal.mutate({ id: dataItem.id })}
					runStatus={run?.status}
					status={dataItem.reviewStatus}
				/>
			),
			width: 150,
			minWidth: 150,
			maxWidth: 150
		}
	]

	const columnsState = useDataTableColumnState(columns)
	const publishers = run?.publishers ?? []
	const distributors = run?.distributors ?? []

	// The three panels, resolved before the tree: nesting a chain of ternaries
	// this deep inside the JSX is what made this component unreadable.
	let panel: ReactNode = null
	if (tab === 'proposte') {
		panel = (
			<>
				<RunNotices
					adottatoMissing={run.adottatoLoaded === false}
					segnaliAssenti={run.segnaliAssenti}
					zeroStock={run.zeroStock}
				/>
				<WorkspaceToolbar
					bulkPending={bulkAccept.isPending}
					distributors={distributors}
					editable={editable}
					onBulkAccept={() => bulkAccept.mutate([...selectedIds])}
					publishers={publishers}
					selectedCount={selectedIds.size}
				>
					<AcceptViewControl
						onAccepted={() => setSelectedIds(new Set())}
					/>
				</WorkspaceToolbar>
				<div className='flex min-h-0 flex-1 animate-enter px-4 pb-3 [--enter-step:3] md:px-6'>
					{/* On narrow screens the open detail takes the whole width. */}
					<div
						className='flex min-w-0 flex-1 flex-col data-[detail-open=true]:max-lg:hidden'
						data-detail-open={params.row !== undefined}
					>
						<DataTable
							columns={columns}
							columnsState={columnsState}
							data={proposalsQuery.data?.data ?? EMPTY_PROPOSALS}
							onRowClick={(row) => openRow(row.id)}
							pagination={{
								pageIndex: params.page - 1,
								pageCount:
									proposalsQuery.data?.meta.totalPages ?? 1,
								onPageChange: (next) =>
									navigate({
										search: (prev) => ({
											...prev,
											page: next + 1
										}),
										replace: true
									}),
								pageSize,
								onPageSizeChange: (next) => {
									setPageSize(next)
									navigate({
										search: (prev) => ({
											...prev,
											page: 1
										}),
										replace: true
									})
								}
							}}
							rowClassName={(row) =>
								reviewRowClass(row, params.row)
							}
							rowHeight={56}
							selection={{
								selectedIds,
								onSelectionChange: setSelectedIds,
								isRowSelectable: (row) =>
									editable && row.reviewStatus === 'pending',
								notSelectableTooltip: t(
									'po:grid.not_bulk_selectable'
								)
							}}
							sort={{
								state: {
									by: params.by,
									direction: params.direction
								},
								onSortChange: (next) =>
									navigate({
										search: (prev) => ({
											...prev,
											by: next.by,
											direction: next.direction,
											page: 1
										}),
										replace: true
									})
							}}
							state={{
								loading: proposalsQuery.isPending,
								error: proposalsQuery.isError,
								empty: {
									icon: <SearchX />,
									title: t('po:grid.empty_title'),
									description: t('po:grid.empty_description')
								}
							}}
						/>
					</div>
					{params.row ? (
						<ProposalDetailPanel
							accepting={acceptProposal.isPending}
							editable={editable}
							onAccept={(id) => acceptProposal.mutate({ id })}
							onClose={() =>
								navigate({
									search: (prev) => ({
										...prev,
										row: undefined
									}),
									replace: true
								})
							}
							onCorrect={(proposal, qty) =>
								setCorrection({ proposal, newQty: qty })
							}
							proposalId={params.row}
						/>
					) : null}
				</div>
			</>
		)
	} else if (tab === 'correzioni') {
		panel = <CorrectionsWorkspace runId={runId} />
	} else if (tab === 'export') {
		panel = <DocumentExportsTab runId={runId} />
	}

	return (
		<>
			<div className='flex h-full min-h-0 flex-col'>
				{run ? (
					<>
						<div className='border-b'>
							<WorkspaceHeader
								onExport={() => setExportOpen(true)}
								onRename={() => setRenameOpen(true)}
								run={run}
							/>
							{generated ? (
								<RunTabs
									onChange={(tab) =>
										navigate({
											search: (prev) => ({
												...prev,
												tab
											}),
											replace: true
										})
									}
									value={params.tab}
								/>
							) : null}
						</div>
						<RunState run={run} />
					</>
				) : null}

				{panel}
			</div>

			{correction ? (
				<CorrectionDialog
					newQty={correction.newQty}
					onConfirm={(note) =>
						correctProposal.mutate({
							id: correction.proposal.id,
							correctedQty: correction.newQty,
							note
						})
					}
					onOpenChange={(open) =>
						setCorrection(open ? correction : null)
					}
					pending={correctProposal.isPending}
					proposal={correction.proposal}
				/>
			) : null}
			{run ? (
				<RenameRunDialog
					onOpenChange={setRenameOpen}
					open={renameOpen}
					run={run}
				/>
			) : null}
			{run ? (
				<ExportDialog
					filters={{
						search: params.search || undefined,
						publisher: params.publisher,
						distributor: params.distributor,
						tipologia: params.tipologia,
						review: params.review
					}}
					onOpenChange={setExportOpen}
					open={exportOpen}
					publishers={publishers}
					runId={runId}
				/>
			) : null}
		</>
	)
}

// Document-level notices, stacked above the toolbar.
function RunBanner(props: {
	tone: keyof typeof NOTICE_TONES
	children: ReactNode
}) {
	return (
		<div
			className={`animate-enter border-b px-4 py-2 text-sm [--enter-step:2] md:px-6 ${NOTICE_TONES[props.tone]}`}
		>
			{props.children}
		</div>
	)
}

// `segnaliAssenti` is null on documents generated before the check: nothing
// to say about them.
function RunNotices(props: {
	zeroStock: boolean
	adottatoMissing: boolean
	segnaliAssenti: PoRun['segnaliAssenti']
}) {
	const { t } = useTranslation()

	return (
		<>
			{props.zeroStock ? (
				<RunBanner tone='sky'>
					{t('po:detail_page.simulation_banner')}
				</RunBanner>
			) : null}
			{props.adottatoMissing ? (
				<RunBanner tone='amber'>
					{t('po:detail_page.adottato_missing')}
				</RunBanner>
			) : null}
			{props.segnaliAssenti && props.segnaliAssenti.length > 0 ? (
				<RunBanner tone='amber'>
					{t('po:detail_page.signals_missing', {
						list: props.segnaliAssenti.join(', ')
					})}
				</RunBanner>
			) : null}
		</>
	)
}

// Row tint by review state. Pending rows stay clean (with 14k pending, tinting
// them all is a wall of colour that guides nothing) — the eye rests on the
// work still to do. Reviewed rows recede into a faint green, so progress reads
// as green filling in from the top.
// Every tint is opaque — mixed into the card colour rather than laid over it
// with transparency — because pinned cells inherit the row background and
// must cover the cells scrolling underneath them.
const REVIEW_ROW_TINT: Record<string, string> = {
	pending: '',
	corrected:
		'bg-[color-mix(in_oklab,var(--color-emerald-50)_60%,var(--card))]',
	accepted: 'bg-emerald-50'
}

// The row open in the detail panel stands out; every other row keeps its
// review tint.
function reviewRowClass(row: PoProposal, openRowId: string | undefined) {
	return row.id === openRowId
		? 'bg-[color-mix(in_oklab,var(--primary)_10%,var(--card))]'
		: REVIEW_ROW_TINT[row.reviewStatus]
}

// The two ways of looking at a generated document. A nav, not a container:
// the active one lives in the URL so a filtered grid or an export list stays
// shareable and survives a reload.
function RunTabs(props: {
	value: (typeof TABS)[number]
	onChange: (tab: (typeof TABS)[number]) => void
}) {
	const { t } = useTranslation()

	return (
		<div className='animate-enter px-4 [--enter-step:1] md:px-6'>
			<Tabs onValueChange={props.onChange} value={props.value}>
				<TabsList variant='line'>
					<TabsTrigger value='proposte'>
						{t('po:tabs.proposals')}
					</TabsTrigger>
					<TabsTrigger value='correzioni'>
						{t('po:tabs.corrections')}
					</TabsTrigger>
					<TabsTrigger value='export'>
						{t('po:tabs.exports')}
					</TabsTrigger>
				</TabsList>
			</Tabs>
		</div>
	)
}

// Fills the empty workspace while a run is generating or after it failed —
// honest state (contract §4.1 — mai ambiguo): a live spinner, or the reason.
// A failed document keeps its reason once archived.
function RunState(props: {
	run: { status: string; error: string | null; engineVersion: string | null }
}) {
	const { t } = useTranslation()

	// A generated document shows its tabs instead.
	if (props.run.engineVersion !== null) {
		return null
	}

	if (props.run.status !== 'pending' && props.run.status !== 'running') {
		return (
			<Empty className='min-h-0 flex-1 animate-enter border-none [--enter-step:1]'>
				<EmptyHeader>
					<EmptyMedia
						className='size-14 bg-destructive/10 text-destructive'
						variant='icon'
					>
						<AlertTriangle className='size-6' />
					</EmptyMedia>
					<EmptyTitle>{t('po:detail_page.failed_title')}</EmptyTitle>
					<EmptyDescription>
						{props.run.error ?? t('po:detail_page.failed_unknown')}
					</EmptyDescription>
				</EmptyHeader>
			</Empty>
		)
	}

	return (
		<Empty className='min-h-0 flex-1 animate-enter border-none [--enter-step:1]'>
			<EmptyHeader>
				<div className='relative mb-2 flex size-16 items-center justify-center'>
					<span className='absolute inset-0 animate-ping rounded-full bg-primary/15' />
					<span className='absolute inset-2 rounded-full bg-primary/10' />
					<Spinner className='relative size-7 text-primary' />
				</div>
				<EmptyTitle>{t('po:detail_page.generating_title')}</EmptyTitle>
				<EmptyDescription>
					{t('po:detail_page.generating')}
				</EmptyDescription>
			</EmptyHeader>
		</Empty>
	)
}
